import React, { useEffect, useRef } from 'react';
import { loadGoogleMaps } from '../googleMaps';
import { useTranslation } from '../i18n';

// Make sure the Google suggestions dropdown floats above modals (which use
// z-index ~1000). Injected once.
function ensurePacStyle() {
  if (document.getElementById('ms-pac-style')) return;
  const el = document.createElement('style');
  el.id = 'ms-pac-style';
  el.textContent = '.pac-container{z-index:100000 !important;border-radius:10px;margin-top:2px;box-shadow:0 8px 24px rgba(0,0,0,0.14);font-family:-apple-system,BlinkMacSystemFont,sans-serif;}';
  document.head.appendChild(el);
}

// Parse a Google Place into our split street / "City, ST ZIP" shape.
function parsePlace(place) {
  const c = {};
  (place.address_components || []).forEach((comp) => {
    comp.types.forEach((tp) => { c[tp] = { long: comp.long_name, short: comp.short_name }; });
  });
  const streetNum = c.street_number?.long || '';
  const route = c.route?.long || '';
  const street = [streetNum, route].filter(Boolean).join(' ');
  const cityName =
    c.locality?.long ||
    c.sublocality?.long ||
    c.postal_town?.long ||
    c.administrative_area_level_2?.long ||
    '';
  const state = c.administrative_area_level_1?.short || '';
  const zip = c.postal_code?.long || '';
  const cityLine = [cityName, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return { street: street || place.name || '', city: cityLine };
}

// A street-address input wired to Google Places Autocomplete. Suggestions
// appear as the user types; selecting one fills street + city. If Google has
// no match the user just keeps typing — manual entry always works.
export default function AddressAutocomplete({
  value,
  onStreetChange,
  onSelect,
  style,
  placeholder,
  name = 'street',
  id,
  ariaLabel
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ensurePacStyle();
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current || acRef.current) return;
        if (!google.maps.places || !google.maps.places.Autocomplete) return;
        acRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'name']
        });
        acRef.current.addListener('place_changed', () => {
          const place = acRef.current.getPlace();
          if (place && place.address_components && onSelect) {
            onSelect(parsePlace(place));
          }
        });
      })
      .catch(() => {
        /* No key / API blocked → silently fall back to a plain manual input. */
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      ref={inputRef}
      id={id}
      style={style}
      name={name}
      placeholder={placeholder}
      aria-label={ariaLabel || t('a11y.street_address')}
      // The Google Places widget renders its own .pac-container dropdown
      // OUTSIDE our React tree, so we can't wire role="combobox" to a listbox
      // we don't own (aria-controls/aria-activedescendant would dangle). An
      // incomplete combobox role misleads screen readers more than no role, so
      // we expose a plainly-labelled text input; Google's widget handles arrow/
      // Enter navigation of suggestions natively.
      value={value}
      onChange={(e) => onStreetChange(e.target.value)}
      // Enter selects a Google suggestion — stop it from also submitting the
      // half-filled form (which produced the "required fields" error).
      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
      autoComplete="off"
    />
  );
}
